import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';

import { useDisclosure, useToast } from '@chakra-ui/react';

import { createGroup } from 'api/groups';
import { useMutation, useQueryClient } from 'react-query';

import { washHub } from 'store/slices';

import { RefType, AddGroupModalProps } from './AddGroupModal.props';

import AddGroupModalView from './AddGroupModal.view';
import ObjectID from 'bson-objectid';

const Container = forwardRef<RefType, AddGroupModalProps>((props, ref) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [groupId, setGroupId] = useState('');

  const washHubs = washHub.selectors.useAllWashHubs();
  const washHubId = washHub.selectors.useSelectedWashHubId();

  const { isOpen, onOpen, onClose: _onClose } = useDisclosure();

  const onClose = () => {
    _onClose();
    setGroupId('');
  };

  useImperativeHandle(
    ref,
    () => ({
      onOpen: () => {
        const newObjectId = new ObjectID().toHexString();
        setGroupId(newObjectId);

        onOpen();
      },
    }),
    [onOpen],
  );

  const { mutate, isLoading: isCreating } = useMutation(createGroup, {
    onSuccess: () => {
      toast({
        title: 'Group created',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      queryClient.invalidateQueries({
        queryKey: [`fetch-groups-${washHubId}`],
      });

      onClose();
    },
  });

  const _props = {
    isOpen,
    onClose,
    washHubs,
    groupId,
    isCreating,
    onCreate: async (payload: any) => {
      mutate({ ...payload, id: groupId });
    },
  };

  return <AddGroupModalView {..._props} />;
});

const AddGroupModal = memo(Container);

export default AddGroupModal;
